<script setup>
import Icon from '../Icon.vue'

import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  showLabel: {
    type: Boolean,
    default: false,
  },
  orientation: {
    type: String,
    default: 'vertical',
  },
  modelValue: null,
  otherClass: {
    type: String,
    default: '',
  },
  isSearchable: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  maxHeight: {
    type: String,
    default: '300px',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * Multi-select: modelValue becomes an array, picking toggles instead of
   * replacing, and the panel stays open. Off by default so every existing
   * single-select usage behaves exactly as before.
   */
  multiple: {
    type: Boolean,
    default: false,
  },
  /** Placeholder shown in multi-select mode when nothing is picked yet. */
  emptyLabel: {
    type: String,
    default: '',
  },
})

const emits = defineEmits(['update:modelValue', 'updateAction', 'loadMore'])

const isExpanded = ref(false)
const isFocused = ref(false)
const select = ref(null)
const search = ref('')

const optionValue = (option) =>
  typeof option === 'string' || typeof option === 'number' ? option : option.value

const optionLabel = (option) =>
  typeof option === 'string' || typeof option === 'number' ? option : option.label

/** Picked values in multi-select mode, always an array. */
const picked = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : []))

const isPicked = (option) => picked.value.includes(optionValue(option))

const selection = computed(() => {
  if (props.multiple) {
    if (!picked.value.length) return props.emptyLabel || props.label
    // Name them while the list is short enough to read, then fall back to a
    // count so the closed field can't outgrow the panel it sits in.
    if (picked.value.length <= 2) {
      return props.options
        .filter((option) => isPicked(option))
        .map((option) => optionLabel(option))
        .join(', ')
    }
    return `${picked.value.length} selected`
  }
  if (props.modelValue == null) return props.label
  if (typeof props.options[0] === 'string' || typeof props.options[0] === 'number')
    return props.modelValue || props.label
  else {
    if (!props.modelValue) return props.label
    const found = props.options.find((option) => option.value === props.modelValue)
    return found?.label || props.label
  }
})

const setState = () => {
  isExpanded.value = !isExpanded.value
  if (!isFocused.value) isFocused.value = true
}

const setSelection = (option) => {
  if (option) {
    if (props.multiple) {
      // Stay open: picking several drones one at a time should not cost a reopen
      // per pick.
      const next = picked.value.includes(option)
        ? picked.value.filter((value) => value !== option)
        : [...picked.value, option]
      emits('update:modelValue', next)
      emits('updateAction', next)
      return
    }
    // selection.value = props.options[i];
    isExpanded.value = false
    emits('update:modelValue', option)
    emits('updateAction', option)
  }
}

const closeDropdown = (el) => {
  if (select.value && !select.value.contains(el.target)) {
    isExpanded.value = false
    isFocused.value = false
  }
}

onMounted(() => {
  window.addEventListener('click', closeDropdown)
})

onBeforeUnmount(() => {
  window.removeEventListener('click', closeDropdown)
})
const SCROLL_THRESHOLD = 50
const handleDropdownScroll = (e) => {
  const el = e.target
  if (!el || props.loading) return
  if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD) {
    emits('loadMore')
  }
}

const filteredOptions = computed(() => {
  if (!props.isSearchable) return props.options

  return props.options.filter((option) => {
    const searchTerm = search.value.toLowerCase()
    if (typeof option === 'string') {
      return option.toLowerCase().includes(searchTerm)
    }
    return (
      option.label?.toLowerCase().includes(searchTerm) ||
      option.value?.toLowerCase().includes(searchTerm)
    )
  })
})
</script>

<template>
  <div
    tabindex="0"
    class="select-component"
    :class="{
      vertical: orientation === 'vertical',
      horizontal: orientation === 'horizontal',
      disabled: disabled,
    }"
    ref="select"
  >
    <label v-show="showLabel" :for="label" class="select-label control-label">{{ label }}</label>
    <div class="select-container">
      <div
        :id="label.includes(' ') ? label.split(' ').join('-') : label"
        class="select-field bg-[var(--Background-Input)]"
        :class="`${otherClass} ${isFocused ? 'focus' : ''}`"
        @click="setState"
      >
        <p class="value truncate">
          {{ selection }}
        </p>
        <Icon
          :name="`arrow_head_${isExpanded && isFocused ? 'up' : 'down'}`"
          :size="12"
          color="#E4E4E4"
          @click.stop="setState"
        />
      </div>
      <div v-if="isExpanded && isFocused" class="drop-down">
        <div v-if="isSearchable" class="search-container">
          <Icon name="search" :size="14" color="#E4E4E4" />
          <input v-model="search" placeholder="Search" type="text" />
        </div>
        <div class="dropdown-list" @scroll="handleDropdownScroll">
          <p
            v-for="option in filteredOptions"
            :key="optionValue(option)"
            class="option"
            :class="{ 'option--multi': multiple, 'option--picked': multiple && isPicked(option) }"
            tabindex="0"
            @click="setSelection(optionValue(option))"
          >
            <span v-if="multiple" class="option-check">
              <Icon v-if="isPicked(option)" name="check" :size="10" color="#fff" />
            </span>
            <span class="truncate">{{ optionLabel(option) }}</span>
          </p>
          <p v-if="loading" class="option loading-indicator">Loading more…</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.search-container {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #e4e4e4;
  margin: 10px 6px;
  font-size: 14px;
}
.select-component {
  display: flex;
}

.select-component.vertical {
  flex-direction: column;
  gap: var(--gap-4xs, 4px);
  flex-grow: 1;
}

.select-component.horizontal {
  gap: var(--gap-2xs, 8px);
  align-items: center;
  align-self: stretch;
  justify-content: space-between;
}

.select-label {
  align-self: stretch;
}

.select-component.horizontal .select-label {
  width: 40%;
}

.select-container {
  position: relative;
  /* flex: 1 0 0; */
}

.select-container .select-field {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 6px;
  border-radius: 4px;
  /* background-color: var(--Background-Input); */
  cursor: pointer;
}

.select-container .select-field.focus {
  outline: 1px solid var(--Border-Input--focused);
}

.value {
  /* align-self: stretch; */
  color: var(--idi-shade6-100);
  font-size: 11px;
  letter-spacing: 0.33px;
  line-height: 1;
  font-weight: 400;
  flex: 1 0 0;
}
.disabled {
  opacity: 0.3;
  pointer-events: none;
}
.drop-down {
  position: absolute;
  top: calc(100% + 8px);
  width: 100%;
  padding: var(--gap-2xs, 8px);
  border-radius: 6px;
  background: var(--idi-shade0-100);
  z-index: 10;
}
.dropdown-list {
  min-height: 55px;
  max-height: v-bind(maxHeight);
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.option {
  padding: var(--gap-3xs, 6px);
  align-self: stretch;
  cursor: pointer;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.33px;
  color: var(--idi-shade6-85);
}

.option:hover {
  background: var(--idi-shade2-50);
}

.option--multi {
  display: flex;
  align-items: center;
  gap: var(--gap-2xs, 8px);
}

.option-check {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  border: 1px solid var(--idi-shade4-50, rgba(149, 152, 156, 0.5));
  display: flex;
  align-items: center;
  justify-content: center;
}

.option--picked .option-check {
  background: var(--idi-green, #1b92a6);
  border-color: var(--idi-green, #1b92a6);
}

.option--picked {
  color: var(--idi-shade7-100, #fff);
}

.loading-indicator {
  text-align: center;
  opacity: 0.5;
  cursor: default;
  pointer-events: none;
}
</style>
